<template>
    <div id="chat">
        <div class="chat-box-container" :style="{ top: `${position.top}px`, left: `${position.left}px` }">
            <div class="chat-header" @mousedown="startDrag">
                Locate2u AI
                <div class="close_container" @click="closeChat">
                    <md-icon class="close_icon">close</md-icon>
                </div>
            </div>
            <div class="chat-body" ref="chatBody">
                <div v-for="(message, index) in messages" :key="index">
                    <div :class="['message', message.role === 'User' ? 'user-message' : 'bot-message']">
                        <template v-if="message.role === 'bot-typing'">
                            <div class="typing-bubble">
                                <span></span>
                                <span></span>
                                <span></span>
                            </div>
                        </template>
                        <template v-else>
                            <div v-html="compiledMarkdown(message.text)"></div>
                        </template>
                    </div>
                    <div
                        v-if="message.timeSent"
                        :class="['message', message.role === 'User' ? 'user-message-time' : 'bot-message-time']"
                    >
                        {{ message.timeSent.format(dateTypes.standardTime) }}
                    </div>
                </div>
            </div>
            <div class="chat-footer">
                <input
                    v-model="newMessage"
                    type="text"
                    placeholder="Ask a question..."
                    @keydown.enter="sendMessage"
                    class="chat-input"
                    ref="inputElement"
                />
                <button @click="sendMessage" class="send-button">Send</button>
            </div>
        </div>
    </div>
</template>
<script>
import { handleRequests } from '@/helpers';
import { ChatBotPositioningService } from '@/services/ChatBotPositioningService';
import { ServerActionMixin } from '@/mixins';
import { DATE_TYPES } from '@/utils/constants';
import { mapGetters } from 'vuex';
import marked from 'marked';
import moment from 'moment';

export default {
    name: 'ChatBotLayout',
    components: {},
    mixins: [ServerActionMixin],
    data() {
        return {
            messages: [],
            newMessage: '',
            isBotTyping: false,
            cognitiveSwitch: false,
            tokensUsed: 0,
            routeId: this.$route.meta.security.id,
            position: { top: 0, left: 0 },
            isDragging: false,
            dragStart: { x: 0, y: 0 },
            elementStart: { top: 0, left: 0 },
            previousWidth: window.innerWidth,
            previousHeight: window.innerHeight,
            chatBotPositioningService: {},
            dateTypes: DATE_TYPES
        };
    },
    mounted() {
        this.chatBotPositioningService = new ChatBotPositioningService();

        this.centerChat();
        window.addEventListener('resize', this.handleResize);

        this.$nextTick(() => {
            if (this.$refs.inputElement) {
                this.$refs.inputElement.focus();
            }
        });

        let botMessage = '';
        switch (this.routeId) {
            case 'rates-engine':
                botMessage = 'Hello again, I see you are in the Rates Engine, I am here to help where possible.';
                break;
            default:
                botMessage = 'Welcome, I am your assistant, How can I help?';
        }

        this.messages.push({
            text: botMessage,
            role: 'System',
            timeSent: moment()
        });

        if (this.chatBotPositioningService.isChatBotFirstTimeOpened()) {
            this.messages.push({
                text: 'You can access me by the menu or by pressing `SHIFT + ENTER` this will open and close the chat.',
                role: 'System',
                timeSent: moment()
            });

            this.chatBotPositioningService.setChatBotFirstTimeOpened();
        }
    },
    beforeUnmount() {
        window.removeEventListener('resize', this.handleResize);
    },
    computed: {
        ...mapGetters({
            user: 'user/user'
        })
    },
    methods: {
        compiledMarkdown(message) {
            if (message === undefined) 
                return null;

            const render = new marked.Renderer();

            render.link = (href, title, text) => {
                return `<a href="${href}" target="_blank" rel="noopener noreferrer" style="color: blue; text-decoration: underline;">${text}</a>`;
            };

            // eslint-disable-next-line func-names
            render.paragraph = function(message) {
                return `<p style="margin: 0px">${message}</p>`;
            };

            return marked(message, { renderer: render });
        },
        async sendMessage() {
            if (this.newMessage.trim()) {
                this.messages.push({ text: this.newMessage, role: 'User', timeSent: moment() });
                this.newMessage = '';
                this.showBotTyping();

                await this.sendToAIBot();
                this.scrollToBottom();
            }
        },
        scrollToBottom() {
            if (this.$refs.chatBody) {
                this.$nextTick(() => {
                    this.$refs.chatBody.scrollTop = this.$refs.chatBody.scrollHeight;
                });
            }
        },
        async sendToAIBot() {
            try {
                const dataJson = await this.$store.dispatch('chatBot/GET_CHATBOT_PARAMS');
                const scope = this.$router.currentRoute.meta?.security?.chatbotScope ?? null;
                const endpoint = `/api/chatbot/question`;

                const data = {
                    method: 'POST',
                    data: {
                        messageHistories: this.messages.filter((x) => x.role !== 'bot-typing'),
                        data: dataJson !== null ? JSON.stringify(dataJson) : null,
                        tag: scope
                    }
                };

                const response = await handleRequests(endpoint, data);
                this.removeBotTyping();
                this.$_actionHandler(response.data?.action);

                if (response.data?.action?.links && response.data?.action?.links.length > 0) {
                    const markdownLinks = response.data.action.links
                        .map((link) => `[${link.text}](${link.url})`)
                        .join(', ');
                    response.data.aiSummary = `${response.data.aiSummary} ${markdownLinks}`;
                }

                this.tokensUsed += response.data.tokensUsed;
                this.messages.push({
                    text: response.data.aiSummary,
                    role: 'System',
                    timeSent: moment()
                });
            } catch (e) {
                this.removeBotTyping();
                this.messages.push({
                    text:
                        'I am having trouble processing your message. Please try again, and if the error persists, try again in a few minutes.',
                    role: 'System',
                    timeSent: moment()
                });
            }
        },
        showBotTyping() {
            this.isBotTyping = true;
            this.messages.push({ role: 'bot-typing' });
            this.scrollToBottom();
        },
        removeBotTyping() {
            this.isBotTyping = false;
            this.messages = this.messages.filter((message) => message.role !== 'bot-typing');
            this.scrollToBottom();
        },
        startDrag(event) {
            this.isDragging = true;
            this.dragStart = { x: event.clientX, y: event.clientY };
            this.elementStart = { ...this.position };

            document.addEventListener('mousemove', this.onDrag);
            document.addEventListener('mouseup', this.stopDrag);
        },
        onDrag(event) {
            if (!this.isDragging) 
                return;

            const deltaX = event.clientX - this.dragStart.x;
            const deltaY = event.clientY - this.dragStart.y;

            const chatBox = this.$el.querySelector('.chat-box-container');
            const windowWidth = window.innerWidth;
            const windowHeight = window.innerHeight;

            this.position.top = Math.min(
                windowHeight - (chatBox.offsetHeight + 10),
                Math.max(0, this.elementStart.top + deltaY)
            );
            this.position.left = Math.min(
                windowWidth - chatBox.offsetWidth / 1.9,
                Math.max(0, this.elementStart.left + deltaX)
            );
            this.chatBotPositioningService.setChatBotWindowPosition(this.position);
        },
        stopDrag() {
            this.isDragging = false;
            document.removeEventListener('mousemove', this.onDrag);
            document.removeEventListener('mouseup', this.stopDragging);
        },
        handleResize() {
            const chatBox = this.$el.querySelector('.chat-box-container');

            const windowWidth = window.innerWidth;
            const windowHeight = window.innerHeight;

            const widthRatio = windowWidth / this.previousWidth;
            const heightRatio = windowHeight / this.previousHeight;

            this.position.top = Math.min(windowHeight - (chatBox.offsetHeight + 10), this.position.top * heightRatio);
            this.position.left = Math.min(windowWidth - chatBox.offsetWidth / 1.9, this.position.left * widthRatio);

            this.previousWidth = windowWidth;
            this.previousHeight = windowHeight;

            this.chatBotPositioningService.setChatBotWindowPosition(this.position);
        },
        centerChat() {
            const chatBox = this.$el.querySelector('.chat-box-container');

            const windowWidth = window.innerWidth;
            const windowHeight = window.innerHeight;

            const chatBotPos = this.chatBotPositioningService.getChatBotWindowPosition();

            if (chatBotPos !== null) {
                this.position.top = chatBotPos.top;
                this.position.left = chatBotPos.left;
            } else {
                this.position.top = (windowHeight - chatBox.offsetHeight) / 7;
                this.position.left = (windowWidth - chatBox.offsetWidth) / 1.5;
            }
        },
        closeChat() {
            this.$emit('closeAiChat');
        }
    }
};
</script>
<style lang="scss" scoped>
@import '@/assets/scss/md/_colors.scss';
.chat-box-container {
    position: absolute;
    top: 10%;
    left: 50%;
    transform: translateY(-50%);
    z-index: 1000;
    background-color: #f9f9f9;
    border: 1px solid #ddd;
    border-radius: 10px;
    display: flex;
    flex-direction: column;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    transform: translateX(-50%);
    min-width: 300px;
    width: 23%;
    min-height: 500px;
    max-height: 500px;
}

.chat-header {
    padding: 10px;
    background-color: $locate2u-blue-base;
    color: white;
    text-align: center;
    font-size: 1.2rem;
    border-top-left-radius: 10px;
    border-top-right-radius: 10px;
    cursor: grab;
    user-select: none;
}

.chat-header:active {
    cursor: grabbing;
}

.remove-margin {
    margin: 0px !important;
}

.chat-body {
    flex: 1;
    padding: 10px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 10px;
    max-height: 400px;
}

.chat-message {
    display: flex;
}

.message {
    max-width: 70%;
    padding: 10px;
    border-radius: 10px;
    font-size: 0.9rem;
}

.user-message {
    justify-self: self-end;
    background-color: #007bff;
    color: white;
    border-radius: 10px;
    padding: 10px;
    max-width: 70%;
    border-top-right-radius: 0;
}
.user-message-time {
    justify-self: self-end;
    padding: 0;
    font-size: x-small;
}

.bot-message {
    justify-self: self-start;
    background-color: #e5e5e5;
    color: black;
    border-radius: 10px;
    border-top-left-radius: 0;
    padding: 10px;
    max-width: 70%;
}

.bot-message-time {
    justify-self: self-start;
    padding: 0;
    font-size: x-small;
}

.chat-footer {
    display: flex;
    padding: 10px;
    border-top: 1px solid #ddd;

    div {
        left: 5px;
    }
}

.chat-input {
    flex: 1;
    padding: 10px;
    border: 1px solid #ccc;
    border-radius: 5px;
    outline: none;
    font-size: 1rem;
}

.send-button {
    padding: 10px 15px;
    margin-left: 10px;
    background-color: $locate2u-blue-base;
    color: white;
    border: none;
    border-radius: 5px;
    cursor: pointer;
}

.send-button:hover {
    background-color: #0056b3;
}

.typing-bubble {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 4px;
    border-top-left-radius: 0;
}

.typing-bubble span {
    width: 8px;
    height: 8px;
    background-color: #ccc;
    border-radius: 50%;
    animation: typing 1.5s infinite ease-in-out;
}

.typing-bubble span:nth-child(1) {
    animation-delay: 0s;
}

.typing-bubble span:nth-child(2) {
    animation-delay: 0.2s;
}

.typing-bubble span:nth-child(3) {
    animation-delay: 0.4s;
}

@keyframes typing {
    0%,
    80%,
    100% {
        transform: scale(0);
        opacity: 0.5;
    }

    40% {
        transform: scale(1);
        opacity: 1;
    }
}

.close_icon {
    float: right;
    color: white !important;
    border-radius: 20%;
}

.close_icon:hover {
    background-color: #0056b3;
}

.close_container {
    display: contents;
}
</style>
